import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { X } from "lucide-react";

// Full-screen camera that reads one QR code and hands its text to onResult. Uses the back camera.
// Closes only with the X (or Escape): on touch screens the opening tap can land again on the overlay.
export default function QrScanner({ title, hint, onResult, onClose }) {
  const video = useRef(null);
  const canvas = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let stream, raf, done = false;
    const tick = () => {
      const v = video.current, c = canvas.current;
      if (done || !v || !c) return;
      if (v.readyState === v.HAVE_ENOUGH_DATA) {
        c.width = v.videoWidth; c.height = v.videoHeight;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(v, 0, 0, c.width, c.height);
        const code = jsQR(ctx.getImageData(0, 0, c.width, c.height).data, c.width, c.height, { inversionAttempts: "dontInvert" });
        if (code?.data) { done = true; onResult(code.data); return; }
      }
      raf = requestAnimationFrame(tick);
    };
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
        if (!video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        raf = requestAnimationFrame(tick);
      } catch (e) {
        setError(e?.name === "NotAllowedError"
          ? "Camera is blocked. Allow the camera for this site in your browser settings, then try again."
          : "Couldn't open the camera on this phone.");
      }
    })();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      done = true;
      cancelAnimationFrame(raf);
      (stream?.getTracks() || []).forEach((t) => t.stop());
      document.removeEventListener("keydown", onKey);
    };
  }, [onResult, onClose]);

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col" role="dialog" aria-modal="true" aria-label={title}
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="flex items-center justify-between p-4">
        <div>
          <div className="text-white font-semibold">{title}</div>
          {hint && <div className="text-xs text-zinc-400">{hint}</div>}
        </div>
        <button type="button" aria-label="Close" onClick={onClose} className="p-2 text-zinc-300"><X className="h-7 w-7" /></button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={video} playsInline muted className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-64 w-64 rounded-2xl border-4 border-white/80" />
        </div>
        {error && <div className="absolute inset-x-4 bottom-8 rounded-lg bg-rose-600/90 p-3 text-sm text-white">{error}</div>}
      </div>
      <canvas ref={canvas} className="hidden" />
    </div>
  );
}
